import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import ImageUploader from './ImageUploader';

/**
 * Reusable SEO & Sharing block.
 *
 * Wraps the three canonical meta fields (Page Title, Meta Description, OG Image)
 * in a collapsible card. Drop this into any manager (Packages, Landing Pages,
 * Itineraries, future pages) — pass the current values + a change handler and
 * you get consistent UX everywhere.
 *
 * Props:
 *   values     : { seo_title, seo_description, seo_og_image }
 *   onChange   : (key, value) => void        — called on every field edit
 *   heading    : optional heading text       — defaults to "SEO Settings"
 *   subheading : optional helper text        — shown below the heading
 *   defaultOpen: boolean                     — collapsed by default (false)
 *   testIdPrefix: string                     — data-testid namespace, e.g. "package-seo"
 */
const SEOSettingsBlock = ({
  values = {},
  onChange,
  heading = 'SEO Settings',
  subheading = 'Overrides the global SEO defaults for this page. Leave empty to fall back to the global settings.',
  defaultOpen = false,
  testIdPrefix = 'seo',
  showRobots = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const desc = values.seo_description || '';
  const remaining = 160 - desc.length;
  const counterColor = desc.length > 160 ? 'text-red-600' : desc.length > 140 ? 'text-amber-600' : 'text-gray-400';
  // seo_no_index / seo_no_follow default to FALSE (i.e. Allow Google to Index +
  // Follow Links ON by default). Keep the toggle state driven by these two
  // boolean fields so a single source of truth (the DB field) remains.
  const allowIndex = !values.seo_no_index;
  const allowFollow = !values.seo_no_follow;

  return (
    <div className="bg-white border border-gray-200 rounded-lg" data-testid={`${testIdPrefix}-block`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-t-lg"
        data-testid={`${testIdPrefix}-toggle`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Search size={14} className="text-[#0d64a8] shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-800 uppercase tracking-widest">{heading}</div>
            <div className="text-[11px] text-gray-500 mt-0.5 truncate">{subheading}</div>
          </div>
        </div>
        {open ? <ChevronDown size={16} className="text-gray-400 shrink-0" /> : <ChevronRight size={16} className="text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100" data-testid={`${testIdPrefix}-body`}>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Page Title</label>
            <input
              value={values.seo_title || ''}
              onChange={(e) => onChange('seo_title', e.target.value)}
              placeholder="Shown as the browser tab title & search result headline"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"
              data-testid={`${testIdPrefix}-title`}
              maxLength={120}
            />
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600">Meta Description</label>
              <span className={`text-[11px] font-mono ${counterColor}`} data-testid={`${testIdPrefix}-desc-counter`}>
                {desc.length}/160
              </span>
            </div>
            <textarea
              value={desc}
              onChange={(e) => onChange('seo_description', e.target.value.slice(0, 160))}
              placeholder="Short teaser (max 160 characters) shown in Google results & link previews"
              rows={3}
              maxLength={160}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"
              data-testid={`${testIdPrefix}-description`}
            />
            {remaining < 0 && <p className="text-[11px] text-red-600 mt-1">Trim by {Math.abs(remaining)} characters — Google truncates over 160.</p>}
          </div>
          <div>
            <ImageUploader
              value={values.seo_og_image || ''}
              onChange={(url) => onChange('seo_og_image', url)}
              label="OG Image"
              hint="Shown on WhatsApp / social previews. Recommended 1200×630 (1.91:1)."
              height="h-40"
            />
          </div>
          {showRobots && (
            <div className="pt-2 border-t border-gray-100 space-y-2">
              <label className="flex items-start gap-3 cursor-pointer" data-testid={`${testIdPrefix}-index-toggle`}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={allowIndex}
                  onClick={() => onChange('seo_no_index', !(!allowIndex))}
                  className={`mt-0.5 relative inline-flex h-5 w-9 items-center rounded-full transition ${allowIndex ? 'bg-[#0d64a8]' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${allowIndex ? 'translate-x-4' : 'translate-x-1'}`} />
                </button>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-gray-800">Allow Google to Index this page</div>
                  <div className="text-[11px] text-gray-500">Off = <span className="font-mono">noindex</span> — page will not appear in Google search.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid={`${testIdPrefix}-follow-toggle`}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={allowFollow}
                  onClick={() => onChange('seo_no_follow', !(!allowFollow))}
                  className={`mt-0.5 relative inline-flex h-5 w-9 items-center rounded-full transition ${allowFollow ? 'bg-[#0d64a8]' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${allowFollow ? 'translate-x-4' : 'translate-x-1'}`} />
                </button>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-gray-800">Follow Links on this page</div>
                  <div className="text-[11px] text-gray-500">Off = <span className="font-mono">nofollow</span> — Google will not follow links from this page.</div>
                </div>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SEOSettingsBlock;
